import { useState } from "react";
import { api, fmtNGN, fmtDate, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { FileText, CircleNotch, CheckCircle, XCircle, Warning } from "@phosphor-icons/react";

/** Staff view of a non-partner-school loan: payee from the invoice, the file itself, and the invoice check. */
export default function LoanInvoicePanel({ loan, canCheck = false, onChange }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const pe = loan.payee;
  if (!loan.invoice_id || !pe) return null;
  const check = loan.invoice_check;

  const view = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/loans/${loan.id}/invoice`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank", "noopener");
    } catch (e) { toast.error("Couldn't open the invoice"); } finally { setBusy(false); }
  };

  const decide = async (approved) => {
    setBusy(true);
    try {
      await api.post(`/admin/loans/${loan.id}/invoice-check`, { approved, note: note || null });
      toast.success(approved ? "Invoice approved for payout" : "Invoice rejected");
      onChange?.();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-[#E5E3DB] bg-white p-3" data-testid={`invoice-panel-${loan.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs uppercase tracking-[0.14em] font-semibold text-[#0A3622] flex items-center gap-1.5"><FileText size={14} weight="fill" /> Non-partner school invoice</div>
        <button type="button" onClick={view} disabled={busy} className="btn-outline !py-1 !px-3 text-xs" data-testid={`invoice-view-${loan.id}`}>
          {busy ? <CircleNotch className="animate-spin" size={12} /> : "View invoice"}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-1.5 text-xs">
        <div>School: <strong>{pe.school_name}</strong></div>
        <div>Invoice ref: <span className="font-mono">{pe.invoice_reference}</span></div>
        <div>Child: <strong>{pe.child_name}</strong> · {pe.class_grade}</div>
        <div>Amount owed: <strong>{fmtNGN(pe.amount_owed)}</strong></div>
        <div className="col-span-2">Pay to: {pe.bank_name} · <span className="font-mono">{pe.account_number}</span> · bank name <strong>{pe.account_name}</strong></div>
        {pe.school_email && <div className="col-span-2">School email: {pe.school_email}</div>}
      </div>
      {pe.name_match < 50 && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-[#B45309]"><Warning size={14} weight="fill" className="mt-0.5" /> Bank account name doesn't clearly match the school name. Check with the school.</div>
      )}
      <div className="mt-3 text-xs" data-testid={`invoice-check-status-${loan.id}`}>
        {check ? (
          <span className={check.status === "approved" ? "text-[#2D7A5D]" : "text-[#B91C1C]"}>
            {check.status === "approved" ? <CheckCircle size={14} weight="fill" className="inline mr-1" /> : <XCircle size={14} weight="fill" className="inline mr-1" />}
            Invoice {check.status} on {fmtDate(check.at)}{check.note ? ` — ${check.note}` : ""}
          </span>
        ) : <span className="text-[#B45309]">Invoice not checked yet. Payout is blocked until a credit officer approves it.</span>}
      </div>
      {canCheck && loan.state !== "disbursed" && (
        <div className="mt-2 space-y-2">
          <input className="input !py-1.5 text-xs" placeholder="Note (e.g. confirmed with school bursar by phone)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`invoice-check-note-${loan.id}`} />
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={() => decide(true)} className="btn-primary !py-1.5 text-xs flex-1" data-testid={`invoice-approve-${loan.id}`}>Approve invoice</button>
            <button type="button" disabled={busy} onClick={() => decide(false)} className="btn-ghost !py-1.5 text-xs" data-testid={`invoice-reject-${loan.id}`}>Reject</button>
          </div>
        </div>
      )}
    </div>
  );
}
